import { sortByArea } from '@/features/session/groupSessions';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FocusShell } from '@/components/layout';
import { Button, Card, Skeleton, StatusPill, Stepper } from '@/components/ui';
import type { Step } from '@/components/ui';
import { useT } from '@/i18n';
import { useSessionPolling } from '@/features/session/useSessionPolling';

const STAGE_2_AT_MS = 5_000;
const STAGE_3_AT_MS = 20_000;

export const Session = () => {
  const t = useT();
  const navigate = useNavigate();
  const { assessmentId = '' } = useParams<{ assessmentId: string }>();
  const { reports, loading, allReady, retry } = useSessionPolling(assessmentId);
  const [stage, setStage] = useState<1 | 2 | 3>(1);

  // Copy-only progress, independent of the actual report statuses — advances on a fixed
  // schedule so the stepper always feels like it's moving, then `allReady` takes over.
  useEffect(() => {
    const toStage2 = setTimeout(() => setStage((s) => (s < 2 ? 2 : s)), STAGE_2_AT_MS);
    const toStage3 = setTimeout(() => setStage((s) => (s < 3 ? 3 : s)), STAGE_3_AT_MS);
    return () => {
      clearTimeout(toStage2);
      clearTimeout(toStage3);
    };
  }, []);

  const childName = reports[0]?.childName ?? '';
  const failedReports = reports.filter((report) => report.status === 'failed');
  const firstReportId = reports[0]?.reportId;

  const steps: Step[] = ([1, 2, 3] as const).map((n) => ({
    label: t(`gen.stage${n}`),
    state: allReady || n < stage ? 'done' : n === stage ? 'current' : 'pending',
  }));

  return (
    <FocusShell>
      <h1 className="text-h1 text-ink">{t('gen.title', { name: childName })}</h1>

      <div className="mt-6">
        <Stepper steps={steps} />
      </div>

      <p className="mt-4 text-caption text-ink-muted">{t('gen.wait')}</p>

      {loading ? (
        <div className="mt-6">
          <Skeleton lines={3} />
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {sortByArea(reports).map((report) => (
            <li key={report.reportId} className="flex items-center justify-between rounded-md bg-sage-100 p-4">
              <span className="text-label text-ink">{report.kind === 'guidance' ? t('guidance.label') : (report.area ?? report.assessmentKey)}</span>
              <StatusPill status={report.status} />
            </li>
          ))}
        </ul>
      )}

      {failedReports.map((report) => (
        <Card key={report.reportId} tone="sage" className="mt-4">
          <p className="text-label text-ink">{t('gen.failed')}</p>
          <p className="mt-1 text-caption text-ink-muted">{t('gen.failedBody')}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void retry(report.reportId)}>
            {t('gen.retry')}
          </Button>
        </Card>
      ))}

      {allReady && firstReportId && (
        <div className="mt-8 space-y-3">
          <Button full onClick={() => navigate(`/reports/${firstReportId}`)}>
            {t('gen.ready')}
          </Button>
          <Button full variant="tertiary" onClick={() => navigate('/reports')}>
            {t('gen.toReports')}
          </Button>
        </div>
      )}
    </FocusShell>
  );
};
